import classNames from 'classnames'
import Link from 'next/link'
import React, { Fragment, useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import { forceCheck } from 'react-lazyload'

import Card from '@/components/Card/Card'
import Spinner from '@/components/Spinner/Spinner'
import { fetchMore } from '@/store/cardlists/actions/fetchMore'
import { CardListType, PageCardType } from '@/types/page'
import useDirectories from '@/utils/useDirectories'

import ContentHint from './ContentHint'
import styles from './Grid.module.scss'

const NUM_CARDS_BEFORE_CONTENT_HINT_DESKTOP = 42
const NUM_CARDS_BEFORE_CONTENT_HINT_MOBILE = 11

type Props = {
  cardLists: CardListType[]
  loading: boolean
  numCols: number
  pageCommander?: PageCardType
  sectionRefs: React.RefObject<HTMLDivElement>[]
  showAds: boolean
}

const Grid = ({ cardLists, loading, numCols, pageCommander, sectionRefs, showAds }: Props) => {
  const [isMounted, setIsMounted] = useState(false)
  const { query } = useDirectories()

  const numCardsBeforeContentHintDesktop = query?.incontent ? 42 : NUM_CARDS_BEFORE_CONTENT_HINT_DESKTOP

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (isMounted) {
      forceCheck()
    }
  })

  let adNextRow = false
  let cardNum = 0
  const isDesktop = numCols > 1
  const elements = [] as JSX.Element[]

  cardLists
    .filter(({ cardviews }) => cardviews.length > 0)
    .forEach(({ cardviews, header, tag, more, href }, i) => {
      let numAds = 0

      // Header
      if (i >= 1) {
        elements.push(
          <>
            <a className={styles.anchor} key={`a-${tag}`} id={tag} />
            <span className={styles.title} key={`span-${tag}`}>
              <Link href={href || `#${tag}`}>{header}</Link>
            </span>
          </>,
        )
      }

      // Cards
      const cardElements = [] as JSX.Element[]
      cardviews.forEach((cardview, i) => {
        const col = (i + 2 * numAds) % numCols
        const rightEdge = col === numCols - 2 // True if double-wide ad would be on the right edge

        cardElements.push(
          <Card card={cardview} className='mb-2' key={i} new={tag === 'newcards'} pageCommander={pageCommander} />,
        )

        if (showAds) {
          if (isDesktop) {
            if (adNextRow && !rightEdge) {
              adNextRow = false
              cardElements.push(
                <Fragment key={`ad-${tag}-${numAds}`}>
                  <ContentHint wide={isDesktop} />
                </Fragment>,
              )
              cardElements.push(<div key={`ad-${tag}-${numAds}-dummy`}></div>)
              numAds += 1
            } else if ((cardNum + 1) % numCardsBeforeContentHintDesktop === 0) {
              if (rightEdge) {
                adNextRow = true
              } else {
                cardElements.push(
                  <Fragment key={`ad-${tag}-${numAds}`}>
                    <ContentHint wide={isDesktop} />
                  </Fragment>,
                )
                cardElements.push(<div key={`ad-${tag}-${numAds}-dummy`}></div>)
                numAds += 1
              }
            }
          } else {
            if ((cardNum + 1) % NUM_CARDS_BEFORE_CONTENT_HINT_MOBILE === 0) {
              cardElements.push(
                <Fragment key={`ad-${tag}-${numAds}-mobile`}>
                  <ContentHint />
                </Fragment>,
              )
              numAds += 1
            }
          }
        }
        cardNum += 1
      })

      elements.push(
        <div
          className={classNames(styles.grid, { 'align-items-center d-flex': !isDesktop })}
          key={`grid-${i}`}
          ref={sectionRefs[i]}
        >
          {cardElements}
        </div>,
      )

      // Load more button
      if (more) {
        elements.push(
          <span className='d-flex justify-content-center mt-2'>
            <Button disabled={loading} onClick={() => fetchMore(i)}>
              {loading ? <Spinner /> : 'Load More'}
            </Button>
          </span>,
        )
      }
    })

  return elements
}

export default Grid
